import L from 'leaflet';

interface MarkerInfo {
  plot_ID: string;
  place: string;
  seasonStart?: { date: string | null; doy: number | null } | null;
  seasonEnd?: { date: string | null; doy: number | null } | null;
  seasonLength?: number | null;
  stationParData?: { breakpoint_1: number; breakpoint_2: number };
  remoteSeasonStart?: number | null;
  remoteSeasonEnd?: number | null;
  remoteSeasonLength?: number | null;
  combinedSeasonStart?: number | null;
  combinedSeasonEnd?: number | null;
  combinedSeasonLength?: number | null;
}

export const getDateFromDOY = (doy: number, year: number): string => {
  const date = new Date(year, 0);
  date.setDate(doy);
  return date.toISOString().split('T')[0];
};

export const createMarker = (
  coordinates: [number, number],
  info: MarkerInfo,
  selectedProduct: string,
  checkedItems: { vegetationSeason?: boolean; vegetationSeasonEnd?: boolean; seasonLength?: boolean },
  selectedYear: number,
  fetchAverageTemperatures: (year: number, plotId: string) => Promise<{ temperatures: number[]; temperatureDays: number[] }>,
  setAverageTemperatures: (temps: number[], days: number[]) => void,
  language: 'cz' | 'en'
): L.Marker => {
  const translations = {
    cz: {
      start: "Začátek sezony",
      end: "Konec sezony",
      length: "Délka sezony",
      days: "dní",
    },
    en: {
      start: "Start of season",
      end: "End of season",
      length: "Season length",
      days: "days",
    },
  }[language];

  const popupContent = `
    <h3>${info.place}</h3>

    ${selectedProduct === "meteorologicalData" && checkedItems.vegetationSeason && info.seasonStart
      ? `<p><strong>${translations.start}:</strong> ${info.seasonStart.date || 'N/A'} (DOY: ${info.seasonStart.doy || 'N/A'})</p>` : ''}

    ${selectedProduct === "meteorologicalData" && checkedItems.vegetationSeasonEnd && info.seasonEnd
      ? `<p><strong>${translations.end}:</strong> ${info.seasonEnd.date || 'N/A'} (DOY: ${info.seasonEnd.doy || 'N/A'})</p>` : ''}

    ${selectedProduct === "meteorologicalData" && checkedItems.seasonLength && info.seasonLength !== null
      ? `<p><strong>${translations.length}:</strong> ${info.seasonLength} ${translations.days} (${translations.start}: ${info.seasonStart?.date || 'N/A'}, ${translations.end}: ${info.seasonEnd?.date || 'N/A'})</p>` : ''}

    ${selectedProduct === "parSensors" ? `
      ${checkedItems.vegetationSeason
        ? `<p><strong>${translations.start}:</strong> ${
          info.stationParData?.breakpoint_1 !== undefined
            ? `DOY ${info.stationParData.breakpoint_1} (${getDateFromDOY(info.stationParData.breakpoint_1, selectedYear)})`
            : 'N/A'
        }</p>` : ''}

      ${checkedItems.vegetationSeasonEnd
        ? `<p><strong>${translations.end}:</strong> ${
          info.stationParData?.breakpoint_2 !== undefined
            ? `DOY ${info.stationParData.breakpoint_2} (${getDateFromDOY(info.stationParData.breakpoint_2, selectedYear)})`
            : 'N/A'
        }</p>` : ''}

      ${checkedItems.seasonLength
        ? `<p><strong>${translations.length}:</strong> ${
          info.stationParData?.breakpoint_1 !== undefined && info.stationParData?.breakpoint_2 !== undefined
            ? `${info.stationParData.breakpoint_2 - info.stationParData.breakpoint_1} ${translations.days}`
            : 'N/A'
        }</p>` : ''}
    ` : ''}

    ${selectedProduct === "dpz" ? `
      ${checkedItems.vegetationSeason
        ? `<p><strong>${translations.start}:</strong> ${
          info.remoteSeasonStart !== null
            ? `DOY ${info.remoteSeasonStart} (${getDateFromDOY(info.remoteSeasonStart!, selectedYear)})`
            : 'N/A'
        }</p>` : ''}

      ${checkedItems.vegetationSeasonEnd
        ? `<p><strong>${translations.end}:</strong> ${
          info.remoteSeasonEnd !== null
            ? `DOY ${info.remoteSeasonEnd} (${getDateFromDOY(info.remoteSeasonEnd!, selectedYear)})`
            : 'N/A'
        }</p>` : ''}

      ${checkedItems.seasonLength
        ? `<p><strong>${translations.length}:</strong> ${
          info.remoteSeasonLength !== null ? `${info.remoteSeasonLength} ${translations.days}` : 'N/A'
        }</p>` : ''}
    ` : ''}

    ${selectedProduct === "combinedProduct" ? `
      ${checkedItems.vegetationSeason
        ? `<p><strong>${translations.start}:</strong> ${
          info.combinedSeasonStart !== null
            ? `DOY ${info.combinedSeasonStart} (${getDateFromDOY(info.combinedSeasonStart!, selectedYear)})`
            : 'N/A'
        }</p>` : ''}

      ${checkedItems.vegetationSeasonEnd
        ? `<p><strong>${translations.end}:</strong> ${
          info.combinedSeasonEnd !== null
            ? `DOY ${info.combinedSeasonEnd} (${getDateFromDOY(info.combinedSeasonEnd!, selectedYear)})`
            : 'N/A'
        }</p>` : ''}

      ${checkedItems.seasonLength
        ? `<p><strong>${translations.length}:</strong> ${
          info.combinedSeasonLength !== null ? `${info.combinedSeasonLength} ${translations.days}` : 'N/A'
        }</p>` : ''}
    ` : ''}

    <img src="/DN_Portal/${info.plot_ID}.jpg" alt="Plot Image" style="width: 100%; height: auto;">
  `;

  const marker = L.marker(coordinates);
  marker.bindPopup(popupContent);

  marker.on('click', () => {
    console.log(`Marker clicked for plot ${info.plot_ID}`);
    fetchAverageTemperatures(selectedYear, String(info.plot_ID)).then(({ temperatures, temperatureDays }) => {
      setAverageTemperatures(temperatures, temperatureDays);
    });
  });

  return marker;
};
